import React from "react";
import type { TableColumnsType } from "antd";
import * as OrderUtils from "@/utils/OrderUtils";
import { Observer } from "mobx-react-lite";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { getAutoRowSpan } from "@/utils/table";
import LoadingIcon from "@/components/LoadingIcon";
import classNames from "classnames";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import MultiPlatformEwaybillResultStore, { EwaybillTableColumnData } from "./MultiPlatformEwaybillResultStore";
import { useGlobalStore } from "@/store/hooks";
import { message } from "antd";
import copy from "copy-to-clipboard";

function useWaybillResultTableColumns(store: MultiPlatformEwaybillResultStore) {
    const globalStore = useGlobalStore();

    const columns: TableColumnsType<EwaybillTableColumnData> = [
        {
            title: "店铺信息",
            width: 100,
            key: "shopId",
            render(value, record, index) {
                const shop = globalStore.shopMapping.get(record.shopId);

                return <span>{shop?.name}</span>;
            },
        },
        {
            title: "收货人信息",
            key: "consignee",
            render(_, value) {
                return (
                    <Observer>
                        {() => {
                            const order = store.orderMapping.get(value.orderId);

                            if (!order) {
                                return null;
                            }

                            const orderOriginalIds: string[] = [];
                            // 如果是合并订单
                            if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
                                order.childOrders?.forEach((childOrder) => {
                                    orderOriginalIds.push(childOrder.originalId);
                                });
                            } else {
                                orderOriginalIds.push(order.originalId);
                            }

                            return (
                                <div>
                                    <p
                                        style={{ wordBreak: "break-all", fontSize: 11, lineHeight: 1 }}
                                        className="text-gray"
                                    >
                                        订单编号:{orderOriginalIds.join(",")}
                                    </p>

                                    <span>{OrderUtils.getConsigneeName(order)}，</span>
                                    <span>{OrderUtils.getConsigneeAddress(order)} </span>
                                    <span>{OrderUtils.getConsigneeMobile(order)} </span>
                                    <span>{OrderUtils.getConsigneeTelephone(order)} </span>
                                </div>
                            );
                        }}
                    </Observer>
                );
            },
            onCell(value, index) {
                return {
                    rowSpan: getAutoRowSpan(store.dataSource, value, index, "orderId"),
                };
            },
        },
        {
            width: 230,
            title: (
                <div>
                    <span>电子面单号获取结果</span>
                    <a
                        className="text-light ml5 small"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();

                            const ewaybills = store.dataSource
                                .map(({ ewaybill }) => ewaybill?.waybillNo)
                                .filter(Boolean);
                            const text = ewaybills.join("\n");

                            if (text) {
                                copy(text);

                                message.success("复制成功");
                            }
                        }}
                    >
                        复制
                    </a>
                </div>
            ),
            key: "ewaybills",
            render(_, value) {
                return (
                    <Observer>
                        {() => {
                            const requestGenerateError = store.requestGenerateErrorResultMapping.get(value.orderId);

                            if (requestGenerateError) {
                                return <span className="text-error">{requestGenerateError}</span>;
                            }

                            if (!value.ewaybill) {
                                if (value.errorMsg) {
                                    return <span className="text-error">{value.errorMsg}</span>;
                                }
                                return null;
                            }

                            const printError = store.waybillNoPrintErrorsMapping.get(value.ewaybill.waybillNo);

                            const printIndex = store.printIndexMapping.get(value.ewaybill?.waybillNo);

                            return (
                                <div>
                                    <span>
                                        {value.ewaybill.waybillNo}

                                        {printIndex ? (
                                            <span
                                                className={classNames({
                                                    "text-error": !!printError,
                                                })}
                                                style={{
                                                    fontSize: 12,
                                                    color: "rgba(0,0,0,0.412)",
                                                    userSelect: "none",
                                                }}
                                            >
                                                <span>{"　"}</span>
                                                {printIndex}
                                                <span>{"　"}</span>
                                            </span>
                                        ) : null}
                                    </span>

                                    {printError ? (
                                        <AutoLoadingButton
                                            className="ml10"
                                            type="link"
                                            onClick={() => {
                                                return store.handleRePrint(value.ewaybill);
                                            }}
                                        >
                                            重打
                                        </AutoLoadingButton>
                                    ) : null}

                                    {value.ewaybill.parentWaybillNo ? (
                                        <p>
                                            <span className="small text-muted">
                                                母号：{value.ewaybill.parentWaybillNo}
                                            </span>
                                        </p>
                                    ) : null}
                                </div>
                            );
                        }}
                    </Observer>
                );
            },
        },
    ];

    if (!store.isGenerateWaybillNoOnly) {
        columns.push({
            width: 150,
            title: "发送到打印机结果",
            key: "printResult",
            render(_, value) {
                return (
                    <Observer>
                        {() => {
                            const requestGenerateError = store.requestGenerateErrorResultMapping.get(value.orderId);

                            if (requestGenerateError) {
                                return null;
                            }

                            if (value.errorMsg) {
                                return null;
                            }

                            const success = store.waybillNoPrintSuccessMapping.get(value.ewaybill?.waybillNo);

                            if (success) {
                                return <CheckCircleFilled className="success" />;
                            }

                            const error = store.waybillNoPrintErrorsMapping.get(value.ewaybill?.waybillNo);

                            if (error) {
                                const hasTutorial = error?.includes("当前无法发送指令");

                                return (
                                    <span className="text-error">
                                        <CloseCircleFilled />
                                        原因：{error}
                                        {hasTutorial ? (
                                            <a
                                                className="ml5"
                                                target="_blank"
                                                href="https://qw15p3s8hv.feishu.cn/wiki/wikcnP2uK23RrtTFGyYWuUn3ILy"
                                            >
                                                查看解决办法
                                            </a>
                                        ) : null}
                                    </span>
                                );
                            }

                            return <LoadingIcon time={0.5} />;
                        }}
                    </Observer>
                );
            },
        });
    }

    return columns;
}

export default useWaybillResultTableColumns;
